import { Breadcrumb, Button, Form, Input, InputNumber } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ServerGatewayInfo } from '@/api/type-server.ts'
import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Divider, message, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { NumberInput } from '@/components/base/NumberInput.tsx'
import { Textarea } from '@/components/base/Textarea.tsx'
import { GhostType, MachineType, PaymentType } from '@/consts/machine.ts'
import { getDeployListPath, useUnifiedContext } from '@/contexts/UnifiedContext'
import { Deployment } from '@/models/deployment.ts'
import { Image } from '@/models/image.ts'
import { Machine } from '@/models/machine.ts'
import { Snapshot } from '@/models/snapshot.ts'
import { MachineSelector } from '@/pages/create-instance/components/machine-selector.tsx'
import { SliderInput } from '@/pages/instance-list/components/SliderInput.tsx'
import {
  EnhancedSnapshot,
  useTeamSnapshots,
} from '@/pages/teams/create-instance/hooks/useTeamSnapshots'

type FormValuesType = {
  name: string
  description: string
  machine: Machine
  fromImageId: number
  fromSnapId: number
  port: number
  cmd: string
  ttlSeconds: number
  maxReplicas: number
}

export function DeploymentCreateSubPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const editingDeployment = location.state?.editingDeployment as Deployment
  const isEditingDeployEnabled = location.state?.isEditingDeployEnabled as boolean
  const [form] = Form.useForm<FormValuesType>()
  const [loading, setLoading] = useState(false)
  const ctx = useUnifiedContext()
  const deployListPath = getDeployListPath(ctx)

  const [type, setType] = useState(() => {
    if (editingDeployment) {
      return editingDeployment.runtimeEnv.hardwareEnv.resource.machineCategory
    }
    return MachineType.GPU
  })
  const period = PaymentType.Hourly
  const [count] = useState(1)
  const [checkedImage, setCheckedImage] = useState<Image>()
  const [checkedSnap, setCheckedSnap] = useState<EnhancedSnapshot>()
  const [isRequiredMachine, setIsRequiredMachine] = useState(false)
  const [gatewayList, setGatewayList] = useState<ServerGatewayInfo[]>([])
  const [machines, setMachines] = useState<Machine[]>([])
  const [images, setImages] = useState<Image[]>([])
  const [snapshots, setSnapshots] = useState<EnhancedSnapshot[]>([])
  const [machine, setMachine] = useState<Machine>()
  const machineSelectorBillingProps = { billingMethod: 'pay as you go' } as Record<string, unknown>

  // Load team snapshots when in team context
  const { snapshots: teamSnapshots } = useTeamSnapshots(machine, count)

  const [selectedGhostType] = useState<GhostType>(() => {
    if (editingDeployment) {
      return editingDeployment.runtimeEnv.systemEnv.fromImageId !== 0
        ? GhostType.Image
        : GhostType.Snapshot
    }
    return GhostType.Image
  })
  const [isInitialSetupDone, setIsInitialSetupDone] = useState(false)

  const { t } = useTranslation('autoDeploy')

  const handleSetCheckedImage = (image?: Image) => {
    setCheckedImage(image)
    form.setFieldsValue({ machine: machine })
    form.setFieldsValue({ fromImageId: image?.id })
    form.setFieldsValue({ fromSnapId: 0 })
  }

  const handleSetCheckedSnap = (snap?: Snapshot) => {
    setCheckedSnap(snap)
    form.setFieldsValue({ machine: machine })
    form.setFieldsValue({ fromImageId: 0 })
    form.setFieldsValue({ fromSnapId: snap?.id })
  }

  const fetchGatewayList = useCallback(() => {
    if (!machine?.domains?.length) return
    setLoading(true)

    API.getGatewayList({
      domain: machine?.domains[0] ?? 0,
      public: true,
      page: 1,
      per_page: 5,
    })
      .then((res) => {
        if (res.code === 0) {
          setGatewayList(res.gateways)
        }
      })
      .finally(() => setLoading(false))
  }, [machine?.domains, setGatewayList])

  useEffect(() => {
    fetchGatewayList()
  }, [fetchGatewayList])

  // Effect for setting basic form values when editing
  useEffect(() => {
    if (editingDeployment) {
      // Set basic form values immediately
      form.setFieldsValue({
        name: editingDeployment.name,
        description: editingDeployment.description,
        port: editingDeployment.gateway.port,
        cmd: editingDeployment.runtimeEnv.systemEnv.cmd,
        ttlSeconds: editingDeployment.ttlSeconds / 60, // Convert seconds to minutes
        maxReplicas: editingDeployment.maxReplicas,
      })
    }
  }, [editingDeployment, form])

  // Effect for setting machine, image, and snapshot once their lists are available
  useEffect(() => {
    if (!editingDeployment || isInitialSetupDone) return

    // Set machine once machines list is loaded
    if (machines && machines.length > 0) {
      const machine = machines.find(
        (m) =>
          m.unitSpec.classTag ===
            editingDeployment.runtimeEnv.hardwareEnv.resource.machineClassTag &&
          m.machineCategory === editingDeployment.runtimeEnv.hardwareEnv.resource.machineCategory,
      )
      if (machine) setMachine(machine)
    }

    const { fromImageId, fromSnapshotId } = editingDeployment.runtimeEnv.systemEnv

    // Set image once images list is loaded
    if (fromImageId !== 0 && images && images.length > 0) {
      const image = images.find((i) => i.id === fromImageId)
      if (image) handleSetCheckedImage(image)
    }

    // Set snapshot once snapshots list is loaded (search both personal and team snapshots)
    if (fromSnapshotId !== 0) {
      const allSnapshots = [...snapshots, ...(teamSnapshots || [])]
      if (allSnapshots.length > 0) {
        const snap = allSnapshots.find((i) => i.id === fromSnapshotId)
        if (snap) handleSetCheckedSnap(snap)
      }
    }

    if (machine && (checkedImage || checkedSnap)) {
      setIsInitialSetupDone(true)
    }
  }, [
    editingDeployment,
    machines,
    images,
    snapshots,
    teamSnapshots,
    form,
    isInitialSetupDone,
    machine,
    checkedImage,
    checkedSnap,
  ])

  function handleSubmit(fieldsValue: FormValuesType) {
    setLoading(true)
    if (gatewayList.length === 0) {
      message.error('No available gateway yet, please choose another machine')
      setLoading(false)
      return
    }

    const gtwResId = gatewayList?.[0]?.resID || editingDeployment.gateway.resID

    let fromImageId =
      fieldsValue?.fromImageId === 0 ? 0 : fieldsValue?.fromImageId || checkedImage?.id
    let fromSnapshotId =
      fieldsValue?.fromSnapId === 0 ? 0 : fieldsValue?.fromSnapId || checkedSnap?.id || 0

    if (fromImageId !== 0) {
      fromSnapshotId = 0
    } else if (fromSnapshotId !== 0) {
      fromImageId = 0
    }

    if (fromImageId === 0 && fromSnapshotId === 0) {
      message.error('Please select an image or snapshot')
      setLoading(false)
      return
    }

    if (editingDeployment) {
      API.updateDeployment({
        res_id: editingDeployment.resID,
        port: fieldsValue.port,
        domain: machine?.domains?.[0] || 0,
        system_env: JSON.stringify({
          fromImageId: fromImageId,
          fromSnapshotId: fromSnapshotId,
          cmd: fieldsValue.cmd || '',
          envs: '',
          extraPorts: [fieldsValue.port],
        }),
        name: fieldsValue.name,
        description: fieldsValue.description,
        gtw_res_id: gtwResId,
        hardware_resource: JSON.stringify({
          cpus: (machine?.unitSpec.cpuCores ?? 0) * count,
          gpus: (machine?.unitSpec.gpuCores ?? 0) * count,
          diskGB: (machine?.unitSpec.diskGB ?? 0) * count,
          gpuName: machine?.unitSpec.gpuName,
          domain: '',
          agentDomains: [machine?.domains?.[0] ?? 0],
          agentDriver: 1,
          cpuName: machine?.unitSpec.cpuName,
          ramMB: (machine?.unitSpec.ramMB ?? 0) * count,
          gpuRamMB: (machine?.unitSpec.gpuRamMB ?? 0) * count,
          attributeTags: 64,
          machineClassTag: `${machine?.unitSpec.classTag}`,
          machineCategory: machine?.machineCategory ?? 0,
          unitQty: count,
        }),
        enabled: isEditingDeployEnabled,
        ttl_seconds: fieldsValue.ttlSeconds * 60, // Convert minutes to seconds
        max_replicas: fieldsValue.maxReplicas,
      }).then((res) => {
        if (res.code === 0) {
          setLoading(false)
          navigate(deployListPath)
        }
      })
    } else {
      API.createDeployment({
        port: fieldsValue.port,
        domain: machine?.domains?.[0] || 0,
        system_env: JSON.stringify({
          fromImageId: fromImageId,
          fromSnapshotId: fromSnapshotId,
          cmd: fieldsValue.cmd || '',
          envs: '',
          extraPorts: [fieldsValue.port],
        }),
        name: fieldsValue.name,
        description: fieldsValue.description,
        gtw_res_id: gtwResId,
        hardware_resource: JSON.stringify({
          cpus: (machine?.unitSpec.cpuCores ?? 0) * count,
          gpus: (machine?.unitSpec.gpuCores ?? 0) * count,
          diskGB: (machine?.unitSpec.diskGB ?? 0) * count,
          gpuName: machine?.unitSpec.gpuName,
          domain: '',
          agentDomains: [machine?.domains?.[0] ?? 0],
          agentDriver: 1,
          cpuName: machine?.unitSpec.cpuName,
          ramMB: (machine?.unitSpec.ramMB ?? 0) * count,
          gpuRamMB: (machine?.unitSpec.gpuRamMB ?? 0) * count,
          attributeTags: 64,
          machineClassTag: `${machine?.unitSpec.classTag}`,
          machineCategory: machine?.machineCategory ?? 0,
          unitQty: count,
        }),
        ttl_seconds: fieldsValue.ttlSeconds * 60, // Convert minutes to seconds
        max_replicas: fieldsValue.maxReplicas,
      }).then((res) => {
        if (res.code === 0) {
          setLoading(false)
          navigate(deployListPath)
        }
      })
    }
  }

  return (
    <>
      <div className="card bg-panel-white">
        <div className="flex items-center space-x-4 mb-4">
          <Link to={deployListPath}>
            <div className="w-8 h-8 icon-btn">
              <LeftArrow />
            </div>
          </Link>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to={deployListPath} className="h6 text-secondary !hover:text-red-600">
                    <Trans i18nKey="title" ns="autoDeploy">
                      Auto Deploy
                    </Trans>
                  </Link>
                ),
              },
              {
                title: (
                  <span className="h6 text-primary">
                    {editingDeployment ? (
                      <Trans i18nKey="createSubPage.editTitle" ns="autoDeploy">
                        Edit Deploy
                      </Trans>
                    ) : (
                      <Trans i18nKey="createSubPage.title" ns="autoDeploy">
                        New Deploy
                      </Trans>
                    )}
                  </span>
                ),
              },
            ]}
            separator={<span className="h6 text-primary">/</span>}
          />
        </div>

        <Divider />

        <Form
          form={form}
          validateTrigger={['onChange', 'onBlur']}
          layout="vertical"
          name="create-scaling-spec-form"
          scrollToFirstError={true}
        >
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.name" ns="autoDeploy">
                  Deploy Name
                </Trans>
              </div>
            }
            id="name"
            name="name"
            className="mb-8"
            rules={[{ required: true, message: t('createSubPage.form.nameRequired') }]}
          >
            <Input
              className="h-11 px-4 py-3 t-sb w-[576px] border-neutral-200"
              placeholder={t('createSubPage.form.namePlaceholder')}
            />
          </Form.Item>
          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="createSubPage.form.description" ns="autoDeploy">
                  Deploy Description
                </Trans>
              </div>
            }
            id="description"
            name="description"
            className="mb-8"
          >
            <Input.TextArea
              className="h-[88px] px-4 py-3 t-sb w-[576px] border-neutral-200"
              placeholder={t('createSubPage.form.descriptionPlaceholder')}
            />
          </Form.Item>

          <div className="mb-8 py-2">
            <span className="h6 pl-2 border-l-4 border-secondary">
              <Trans i18nKey="createSubPage.form.deployTargetTitle" ns="autoDeploy">
                Deploy Target
              </Trans>
            </span>
          </div>

          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.accessMethod" ns="autoDeploy">
                  Access Method
                </Trans>
              </div>
            }
            id="accessMethod"
            name="accessMethod"
            className="mb-8 t-sb"
          >
            <Dropdown
              options={[
                {
                  label: t('createSubPage.form.accessMethodPublic'),
                  value: 'public',
                  className: 't-sb',
                },
                {
                  label: t('createSubPage.form.accessMethodPrivate'),
                  value: 'private',
                  className: 't-sb',
                  disabled: true,
                },
              ]}
            />
          </Form.Item>

          <div id="fromImageId" className="mb-4 t-sb text-with-star">
            <Trans i18nKey="createSubPage.form.instanceImage" ns="autoDeploy">
              Instance & Image
            </Trans>
          </div>
          {isRequiredMachine && !checkedImage && !checkedSnap && (
            <div className="mb-4 text-red-600 t-sb">Please Select a instance and image</div>
          )}

          <Form.Item name="machine" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fromImageId" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fromSnapId" hidden>
            <Input />
          </Form.Item>

          <MachineSelector<EnhancedSnapshot>
            type={type}
            period={period}
            onTypeChange={setType}
            onChange={setMachine}
            count={count}
            checkedImage={checkedImage}
            setCheckedImage={(value) => handleSetCheckedImage(value)}
            checkedSnap={checkedSnap}
            setCheckedSnap={(value) => handleSetCheckedSnap(value as EnhancedSnapshot | undefined)}
            onMachinesChange={setMachines}
            onImagesChange={setImages}
            onSnapshotsChange={setSnapshots}
            selectedMachine={machine}
            selectedGhostType={selectedGhostType}
            workloadTypes={['all']}
            teamSnapshots={ctx.mode === 'team' ? teamSnapshots : undefined}
            {...machineSelectorBillingProps}
          />

          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="createSubPage.form.port" ns="autoDeploy">
                  Port (HTTP/HTTPS)
                </Trans>
              </div>
            }
            name="port"
            rules={[{ required: true, message: t('createSubPage.form.portRequired') }]}
          >
            <InputNumber
              className="h-11 t-sb w-[576px] py-2 border-neutral-200"
              placeholder={t('createSubPage.form.portPlaceholder')}
              min={0}
              max={65535}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="createSubPage.form.cmd" ns="autoDeploy">
                  Start Command
                </Trans>
              </div>
            }
            name="cmd"
          >
            <Textarea className="w-[576px]" placeholder={t('createSubPage.form.cmdPlaceholder')} />
          </Form.Item>

          <div className="mb-8">
            <div className="t-sb mb-2">
              <Trans i18nKey="createSubPage.form.ttlSeconds" ns="autoDeploy">
                Instance Idle Retention Period
              </Trans>
            </div>
            <Form.Item
              name="ttlSeconds"
              initialValue={5}
              rules={[{ required: true, message: t('createSubPage.form.ttlSecondsRequired') }]}
              className="mb-0 w-[576px]"
            >
              <SliderInput
                min={3}
                max={90}
                unit={t('createSubPage.form.ttlSecondsUnit')}
                step={1}
                onChange={() => {}}
                showReset={false}
              />
            </Form.Item>
          </div>

          <div className="flex flex-col gap-y-2">
            <Form.Item
              label={
                <div className="t-sb">
                  <Trans i18nKey="createSubPage.form.maxNumberIns" ns="autoDeploy">
                    Maximum Number of Instances
                  </Trans>
                </div>
              }
              name="maxReplicas"
              initialValue={1}
              rules={[{ required: true, message: t('createSubPage.form.maxReplicasRequired') }]}
              className="mb-0"
            >
              <NumberInput
                value={form.getFieldValue('maxReplicas') || 1}
                onChange={(value) => form.setFieldsValue({ maxReplicas: value })}
                className="w-[264px]"
                min={1}
                max={10}
              />
            </Form.Item>
            <div className="t-sm text-secondary">
              <Trans i18nKey="createSubPage.form.maxReplicasHint" ns="autoDeploy">
                Only integer values between 1 and 10 are supported.
              </Trans>
            </div>
          </div>

          <Form.Item>
            <div className="pt-4 flex items-end justify-end gap-4">
              <Button
                size="large"
                className="h-11 px-4 py-3 t-sb"
                onClick={() => {
                  navigate(deployListPath)
                }}
              >
                <Trans i18nKey="createSubPage.form.cancelBtn" ns="autoDeploy">
                  Cancel
                </Trans>
              </Button>
              <Button
                type="primary"
                className="h-11 px-4 py-3 t-sb"
                size="large"
                loading={loading}
                htmlType={'submit'}
                onClick={() => {
                  form.validateFields().then(() => {
                    if (checkedImage || checkedSnap) {
                      handleSubmit(form.getFieldsValue())
                    } else {
                      setIsRequiredMachine(true)
                      document.getElementById('fromImageId')?.scrollIntoView({ behavior: 'smooth' })
                      return
                    }
                  })
                }}
              >
                <Trans i18nKey="createSubPage.form.confirmBtn" ns="autoDeploy">
                  Confirm
                </Trans>
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </>
  )
}
